<script setup lang="ts">
import {computed} from "vue";
import {Alert, AlertLevel, AlertLine, AlertSource} from "@/medication-checkup/interfaces/alerts";
import {alertLevelTone} from "@/medication-checkup/services/AlertLevelTones";

/**
 * The card of one level of one source: the level as the source names it,
 * how many alerts it holds, the first ones by title and treatments, and the
 * way to the whole list.
 *
 * Every word is served — the level label, the titles, the treatments — the
 * card owns the colour of the level only.
 */

const props = defineProps<{
    source: AlertSource;
    level: AlertLevel;
    lines: AlertLine[];
}>();

const emit = defineEmits<{open: []}>();

/**
 * How many alerts the card lists before handing over to the dialog.
 */
const PREVIEW = 4;

const tone = computed(() => alertLevelTone(props.level.severity));

const preview = computed(() => props.level.alerts.slice(0, PREVIEW));

const treatments = (alert: Alert): string => {
    return alert.line_ids
        .map(id => props.lines.find(line => line.id === id)?.label ?? null)
        .filter((label): label is string => label !== null)
        .join(' + ');
};
</script>

<template>
  <div
    class="w-[340px] h-[330px] shrink-0 flex flex-col rounded-xl shadow-custom overflow-hidden bg-white border border-slate-200 border-t-4"
    :class="tone.border"
  >
    <div class="flex items-center gap-2 px-4 py-2.5">
      <span class="w-2 h-2 rounded-full" :class="tone.dot" />
      <span class="text-sm font-semibold">{{ level.label }}</span>
      <span class="flex-1" />
      <span class="rounded-full px-2 text-xs font-bold leading-tight py-0.5" :class="tone.chip">{{ level.count }}</span>
    </div>

    <ul class="flex-1 min-h-0 flex flex-col overflow-hidden">
      <li
        v-for="alert in preview"
        :key="alert.id"
        class="flex flex-col gap-0.5 px-4 py-2 border-t border-gray-light"
      >
        <span class="text-[13px] font-semibold leading-snug line-clamp-2">{{ alert.title }}</span>
        <span v-if="treatments(alert)" class="text-xs text-muted-foreground truncate">{{ treatments(alert) }}</span>
      </li>
    </ul>

    <button
      type="button"
      class="flex justify-end px-4 py-2.5 border-t border-gray-light text-[13px] font-semibold text-primary hover:underline"
      @click="emit('open')"
    >
      Afficher toutes les alertes
    </button>
  </div>
</template>
